import { Suspense } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { Time } from '@gonfalon/datetime/Time';
import { EnvironmentMarker } from '@gonfalon/environments/EnvironmentMarker';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { TextTruncator } from '@gonfalon/launchpad-experimental/TextTruncator';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { ProgressBar } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';
import { differenceInHours } from 'date-fns';

import type { Metric } from '../types';
import { useSuspenseEventActivityByEventKey } from '../useSuspenseEventActivityByEventKey';

import styles from './MetricLastSeenInfo.module.css';
// Base props for direct usage
interface MetricLastSeenInfoDirectProps {
  lastSeen?: number;
  environmentName?: string;
  environmentColor?: string;
  className?: string;
  hideTooltip?: boolean;
  fallback?: string;
  showEnvironment?: boolean;
  metric?: never; // Exclude metric when using direct props
}

// Props for metric-based usage
interface MetricLastSeenInfoMetricProps {
  metric: Metric;
  className?: string;
  hideTooltip?: boolean;
  fallback?: string;
  showEnvironment?: boolean;
  scopedToEnvironment?: string;
  // Exclude all the props that come from the query when using metric mode
  lastSeen?: never;
  environmentName?: never;
  environmentColor?: never;
}

type MetricLastSeenInfoProps = MetricLastSeenInfoDirectProps | MetricLastSeenInfoMetricProps;

// Internal component that renders the UI
const MetricLastSeenInfoDisplay: React.FC<{
  lastSeen?: number;
  environmentName?: string;
  environmentColor?: string;
  className?: string;
  hideTooltip?: boolean;
  fallback?: string;
  showEnvironment?: boolean;
}> = ({
  lastSeen,
  environmentName,
  environmentColor,
  className,
  hideTooltip,
  fallback = 'No events have been received for this metric',
  showEnvironment = true,
}) => {
  const envSpan = environmentColor ? (
    <span className={clsx(styles.lastSeenEnv, className)}>
      in <EnvironmentMarker color={environmentColor} /> <TextTruncator>{environmentName}</TextTruncator>
    </span>
  ) : (
    <span className={className}>
      in <TextTruncator>{environmentName}</TextTruncator>
    </span>
  );

  const icon =
    lastSeen === undefined
      ? 'circle-dashed'
      : differenceInHours(Date.now(), lastSeen) >= 24
        ? 'half-circle'
        : 'status-active';

  return (
    <div className={styles.lastSeenDesc}>
      <Icon name={icon} className={icon === 'status-active' ? styles.active : ''} size="small" />{' '}
      {lastSeen === undefined ? (
        fallback
      ) : (
        <span className={className}>
          <Time notooltip={hideTooltip} prefix="Seen" fromNow datetime={lastSeen} shortenUnits fuzzySeconds />
        </span>
      )}
      {showEnvironment && environmentName && envSpan}
    </div>
  );
};

// Component that fetches event activity for a metric
function MetricLastSeenInfoWithQuery({
  metric,
  projectKey,
  hideTooltip,
  fallback,
  className,
  showEnvironment,
  scopedToEnvironment,
}: {
  metric: Metric;
  projectKey: string;
  hideTooltip?: boolean;
  fallback?: string;
  className?: string;
  showEnvironment?: boolean;
  scopedToEnvironment?: string;
}) {
  const { data: eventActivity } = useSuspenseEventActivityByEventKey(
    projectKey,
    metric.eventKey ?? metric._versionId,
    metric.kind,
    metric.dataSource.key,
  );

  const relevantEnvironment = scopedToEnvironment
    ? eventActivity.environments?.find((environment) => environment.environmentKey === scopedToEnvironment)
    : eventActivity.environment;

  return (
    <MetricLastSeenInfoDisplay
      lastSeen={relevantEnvironment?.lastSeen}
      environmentName={relevantEnvironment?.name}
      environmentColor={relevantEnvironment?.color}
      hideTooltip={hideTooltip}
      fallback={fallback}
      className={className}
      showEnvironment={showEnvironment}
    />
  );
}

export const MetricLastSeenInfo: React.FC<MetricLastSeenInfoProps> = (props) => {
  const projectKey = useProjectKey();

  // Check if using metric-based mode
  if ('metric' in props && props.metric) {
    const { metric, className, hideTooltip, fallback, showEnvironment, scopedToEnvironment } = props;

    return (
      <Suspense fallback={<ProgressBar aria-label="Loading…" isIndeterminate size="small" />}>
        <ErrorBoundary
          owner="team-metrics"
          severity="medium"
          fallbackRender={(errorProps: FallbackProps) => {
            const error = errorProps.error;
            if (error && error.code === 'not_found') {
              return (
                <MetricLastSeenInfoDisplay
                  hideTooltip={hideTooltip}
                  fallback={fallback}
                  className={className}
                  showEnvironment={showEnvironment}
                />
              );
            } else {
              return (
                <MetricLastSeenInfoDisplay
                  hideTooltip={hideTooltip}
                  fallback="Cannot fetch the last seen time for this metric"
                  className={className}
                  showEnvironment={false}
                />
              );
            }
          }}
        >
          <MetricLastSeenInfoWithQuery
            metric={metric}
            projectKey={projectKey}
            hideTooltip={hideTooltip}
            fallback={fallback}
            className={className}
            showEnvironment={showEnvironment}
            scopedToEnvironment={scopedToEnvironment}
          />
        </ErrorBoundary>
      </Suspense>
    );
  }

  // Direct mode - use provided props
  const { lastSeen, environmentName, environmentColor, className, hideTooltip, fallback, showEnvironment } = props;
  return (
    <MetricLastSeenInfoDisplay
      lastSeen={lastSeen}
      environmentName={environmentName}
      environmentColor={environmentColor}
      className={className}
      hideTooltip={hideTooltip}
      fallback={fallback}
      showEnvironment={showEnvironment}
    />
  );
};
